/*
 * Copyright (c) 2026 Ambits Technologies LLC. All rights reserved.
 *
 * This source code is the proprietary and confidential property of
 * Ambits Technologies LLC. Unauthorized copying, modification, distribution,
 * or use of this software, via any medium, is strictly prohibited.
 *
 * MyAmbits - AI-Powered Location Safety & Social Platform
 * https://myambits.com
 */

/*
 * AppPhoneGate — app-wide hard block that requires every signed-in user to
 * verify a phone number before they can reach any tab. Wraps the authed
 * route tree in App.tsx so the gate fires once at first login and never
 * again (a verified user's snapshot lands on cold launch and the sheet
 * never mounts).
 *
 * Unlike the Friends-feature-specific FriendsPhoneGate, this gate has NO
 * onExit handler. Once you reach it you must verify — there is no
 * "continue without verifying" path. Per CLAUDE.md the Friends gate
 * deliberately spared safety surfaces from the verification requirement
 * (SOS / Walk Me Home / Map must remain reachable even if Phone Auth
 * fails on a particular device). This app-level gate accepts that
 * tradeoff at the user's explicit direction: the friend-graph and
 * SafeCircle features are the load-bearing reason for the app and were
 * unusable without phone-backed identity.
 */

import type { ReactNode } from "react";
import { useVerifiedPhone } from "@/hooks/useVerifiedPhone";
import PhoneVerificationSheet from "@/components/onboarding/PhoneVerificationSheet";

interface Props {
  children: ReactNode;
}

export default function AppPhoneGate({ children }: Props) {
  const { verified, loading } = useVerifiedPhone();

  // While the first RTDB snapshot is in flight we render children — same
  // logic as FriendsPhoneGate. Avoids a verification-sheet flash on cold
  // launch for users who already verified on a prior session.
  if (loading) {
    return <>{children}</>;
  }
  if (verified) {
    return <>{children}</>;
  }
  // Onboarding order is phone → EULA → profile → emergency → discovery. This
  // gate covers the phone step. While unverified we render ONLY the phone
  // sheet — NOT children — so the later OnboardingGate steps (which live in
  // children) don't mount underneath it. This removes the 2B occlusion where
  // ProfileSetupScreen rendered under the z-1000 phone sheet. The SOS hardware
  // path stays reachable because SOSTrigger / SimpleSOSOverlay are siblings of
  // AppPhoneGate in App.tsx, not children. There is no username step in
  // onboarding (phone is the identity); username is requested on-demand inside
  // the Friends/invite flow if/when it is needed.
  return <PhoneVerificationSheet />;
}
